import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { dashboardRequest } from "../providers/aws/client";
import { parseAccentFidelityUsage } from "./accentFidelityUsage";
import type {
  CatalogVoice,
  ProviderVoice,
  SoundVoice,
  SoundVoiceApi,
  SoundVoiceApiPayload,
  SoundVoicesApiPayload,
  SoundVoicesPayload,
  ValidateVoiceApiResponse,
  ValidateVoiceResponse,
  VoiceNormalizedProfile,
} from "./contracts";

export const soundQueryKey = ["sound", "voices"] as const;

export type ValidateCatalogVoiceInput = {
  voice_id: string;
  normalized_profile: VoiceNormalizedProfile;
  quality_status: CatalogVoice["quality_status"];
  render_profile_id: string;
  product_flows: ["b2c"];
  reason: string;
  ticket_reference: string;
};

export async function fetchSoundVoices() {
  const pages: SoundVoicesApiPayload[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | number | null = null;
  do {
    const params = new URLSearchParams({ limit: "100" });
    if (cursor !== null) params.set("cursor", String(cursor));
    const page = await dashboardRequest<SoundVoicesApiPayload>(
      `/sound/voices?${params.toString()}`,
    );
    pages.push(page);
    if (page.pagination.has_more && !page.pagination.next_cursor) {
      throw new Error("sound_voice_inventory_missing_next_cursor");
    }
    if (
      page.pagination.next_cursor &&
      seenCursors.has(String(page.pagination.next_cursor))
    ) {
      throw new Error("sound_voice_inventory_repeated_cursor");
    }
    if (page.pagination.next_cursor) {
      seenCursors.add(String(page.pagination.next_cursor));
    }
    cursor = page.pagination.has_more ? page.pagination.next_cursor : null;
  } while (cursor);
  return normalizeSoundPages(pages);
}

export async function lookupVoiceId(voiceId: string) {
  const payload = await dashboardRequest<SoundVoiceApiPayload>(
    `/sound/voices/${encodeURIComponent(voiceId)}`,
  );
  return {
    voice: providerVoice(payload.voice),
    catalog_status: payload.voice.catalog.status,
    checks: [],
  } satisfies ValidateVoiceResponse;
}

export async function validateCatalogVoice(input: ValidateCatalogVoiceInput) {
  const payload = await dashboardRequest<ValidateVoiceApiResponse>(
    "/sound/voices/validate",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  return {
    voice: payload.provider_voice,
    catalog_status: "uncurated",
    checks: payload.checks,
    candidate_catalog_entry: payload.candidate_catalog_entry,
  } satisfies ValidateVoiceResponse;
}

export function useSoundVoices() {
  return useQuery({
    queryKey: soundQueryKey,
    queryFn: fetchSoundVoices,
    staleTime: 30_000,
    retry: false,
  });
}

export function useLookupVoice() {
  return useMutation({ mutationFn: lookupVoiceId });
}

export function useValidateVoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: validateCatalogVoice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: soundQueryKey }),
  });
}

export function normalizeSoundPages(
  pages: SoundVoicesApiPayload[],
): SoundVoicesPayload {
  const first = pages[0];
  if (!first) throw new Error("sound_voice_inventory_empty_response");
  if (first.auditions.environment.key !== first.environment.key) {
    throw new Error("sound_audition_environment_mismatch");
  }
  const byId = new Map<string, SoundVoiceApi>();
  for (const page of pages) {
    if (page.environment.key !== first.environment.key) {
      throw new Error("sound_voice_inventory_environment_changed");
    }
    for (const voice of page.voices) byId.set(voice.voice_id, voice);
  }
  const activeReadback = first.catalog.applied.orchestrator_readbacks.find(
    (readback) =>
      readback.release_id === first.catalog.active_release?.id &&
      readback.digest === first.catalog.active_release?.digest &&
      ["valid", "applied"].includes(readback.validation_result),
  );
  const summary = first.summary;
  const renderDenominator = summary.recent_render_count;
  const pendingDrafts = first.catalog.pending_drafts.map(normalizePendingDraft);
  return {
    environment: first.environment.key,
    product_meaning: first.environment.product_meaning,
    generated_at: first.generated_at,
    provider_status:
      first.provider.status === "live" ? "ok" : first.provider.status,
    provider_message: first.provider.error_code ?? null,
    cache:
      first.provider.cache_age_seconds == null
        ? undefined
        : {
            age_seconds: first.provider.cache_age_seconds,
            stale: first.provider.stale,
          },
    summary: {
      provider_total: summary.provider_visible_total,
      provider_default: summary.provider_default_count,
      provider_non_default: summary.provider_non_default_count,
      active_b2c: summary.active_b2c_count,
      pending_additions: pendingDrafts.reduce(
        (count, draft) => count + draft.voice_diff.added_count,
        0,
      ),
      pending_removals: pendingDrafts.reduce(
        (count, draft) =>
          count +
          draft.voice_diff.removed_count +
          (draft.voice_diff.disabled_count ?? 0),
        0,
      ),
      pending_drafts: pendingDrafts.length,
      provider_missing_active: summary.provider_missing_active_count,
      metadata_drift: summary.metadata_drift_count,
      disabled_soon: summary.disabled_soon_count,
      recent_generated_selection_rate: summary.recent_generated_selection_rate,
      recent_planned_effective_mismatch_count:
        summary.recent_planned_effective_mismatch_count,
      recent_cleanup_failure_count: summary.recent_cleanup_failure_count,
      recent_per_fragment_fallback_rate: ratio(
        summary.recent_per_fragment_fallback_count,
        renderDenominator,
      ),
      recent_narration_coverage_failure_rate: ratio(
        summary.recent_missing_narration_count,
        renderDenominator,
      ),
      accent_fidelity: parseAccentFidelityUsage(summary.accent_fidelity),
    },
    active_release: first.catalog.active_release,
    applied_readback: first.catalog.active_release
      ? {
          release_id:
            activeReadback?.release_id ?? first.catalog.active_release.id,
          digest: activeReadback?.digest ?? first.catalog.active_release.digest,
          applied:
            first.catalog.applied.status === "applied" && !!activeReadback,
        }
      : null,
    pending_drafts: pendingDrafts,
    render_profiles: first.render_profiles,
    narration_locales: first.narration_locales,
    auditions: first.auditions,
    voices: Array.from(byId.values()).map((voice) =>
      normalizeVoice(voice, first.provider.status),
    ),
  };
}

function normalizePendingDraft(
  draft: SoundVoicesApiPayload["catalog"]["pending_drafts"][number],
) {
  const diff = draft.voice_diff;
  return {
    ...draft,
    voice_diff: {
      catalog_changed: diff?.catalog_changed ?? false,
      added_voice_ids: diff?.added_voice_ids ?? [],
      removed_voice_ids: diff?.removed_voice_ids ?? [],
      changed_voice_ids: diff?.changed_voice_ids ?? [],
      disabled_voice_ids: diff?.disabled_voice_ids ?? [],
      added_count: diff?.added_count ?? 0,
      removed_count: diff?.removed_count ?? 0,
      changed_count: diff?.changed_count ?? 0,
      disabled_count: diff?.disabled_count ?? 0,
    },
  };
}

function normalizeVoice(
  voice: SoundVoiceApi,
  providerStatus: SoundVoicesApiPayload["provider"]["status"],
): SoundVoice {
  const catalog =
    voice.catalog.status === "uncurated"
      ? null
      : {
          voice_id: voice.voice_id,
          display_name: voice.catalog.display_name ?? voice.name,
          enabled: voice.catalog.enabled,
          product_flows: voice.catalog.product_flows,
          normalized_profile: voice.catalog.normalized_profile,
          reference_names: voice.catalog.reference_names,
          quality_status: voice.catalog.quality_status ?? "review",
          render_profile_id: voice.catalog.render_profile_id ?? "unassigned",
          provider_category_at_approval:
            voice.catalog.provider_category_at_approval,
          provider_metadata_digest: voice.catalog.provider_metadata_digest,
        };
  const providerState =
    providerStatus === "unavailable"
      ? "unavailable"
      : providerStatus === "stale"
        ? "stale"
        : voice.catalog.status === "orphaned"
          ? "missing"
          : !voice.available
            ? "unavailable"
            : voice.disable_at_unix !== null
              ? "disabled_soon"
              : "live";
  return {
    ...providerVoice(voice),
    catalog,
    catalog_state: voice.catalog.status,
    provider_state: providerState,
    metadata_drift: voice.catalog.metadata_drift,
    applied: voice.applied.status === "applied",
    usage: {
      selection_count: voice.usage.selection_count,
      per_fragment_fallback_count: voice.usage.per_fragment_fallback_count,
      qc_issue_count: voice.usage.qc_failure_count,
      missing_narration_count: voice.usage.missing_narration_count,
      last_selected_at: voice.usage.last_used_at,
      retained_generated_candidate:
        voice.usage.retained_generated_candidate ?? false,
      design_fingerprint: voice.usage.design_fingerprint ?? null,
      generated_create_count: voice.usage.generated_create_count ?? 0,
      generated_reuse_count: voice.usage.generated_reuse_count ?? 0,
    },
  };
}

function providerVoice(voice: SoundVoiceApi): ProviderVoice {
  return {
    voice_id: voice.voice_id,
    name: voice.name,
    category: voice.category ?? "unknown",
    description: voice.description,
    labels: voice.labels,
    preview_url: voice.preview_url,
    available: voice.available,
    disable_at_unix: voice.disable_at_unix,
    recording_quality: voice.recording_quality,
    provider_metadata_digest: voice.provider_metadata_digest,
    fetched_at: voice.fetched_at,
  };
}

function ratio(numerator: number, denominator: number) {
  return denominator > 0 ? numerator / denominator : null;
}
